import { useTranslation } from "react-i18next";
import { Alert, Button } from "@/shared/ui/core";
import { isApiError } from "@/shared/api/client";

export interface ShopErrorProps {
  error: unknown;
  onRetry: () => void;
  /** `true` mientras el reintento está en vuelo. */
  retrying?: boolean;
  /**
   * Qué no se pudo cargar. Por omisión, el catálogo — que es de donde
   * salió este componente. La ficha de un producto usa el mismo aviso con
   * otro titular: es EXACTAMENTE el mismo fallo (el servidor no responde)
   * y la misma salida (reintentar), así que un segundo componente con el
   * texto cambiado sería un gemelo que hay que mantener dos veces.
   */
  title?: string;
}

/**
 * El aviso de que el catálogo no pudo cargarse.
 *
 * ── Qué se enseña y qué no ───────────────────────────────────────────
 * El `code` del contrato sí (`INTERNAL`, `RATE_LIMITED`): es lo que hay
 * que decirle a quien dé soporte, y es estable. El `message` crudo del
 * servidor NO se pinta como título: puede venir en inglés, o describir un
 * detalle interno que a un cliente no le dice nada. Va debajo, como
 * detalle, y el titular es una frase escrita para una persona.
 *
 * ── Por qué hay un botón ─────────────────────────────────────────────
 * La causa más común es que el backend todavía no estaba levantado.
 * Reintentar lo arregla sin recargar la página ni perder el filtro.
 */
export function ShopError({ error, onRetry, retrying = false, title }: ShopErrorProps) {
  const { t } = useTranslation();
  const code = isApiError(error) ? error.code : null;
  const detail = error instanceof Error ? error.message : null;

  return (
    <Alert variant="error" title={title ?? t("No pudimos cargar el catálogo")}>
      <p className="leading-relaxed">
        {t("Vuelve a intentarlo en un momento. Si el problema sigue, escríbenos.")}
      </p>

      {(code || detail) && (
        <p className="mt-2 font-mono text-[11px] opacity-80">
          {code}
          {code && detail ? " · " : ""}
          {detail}
        </p>
      )}

      <Button
        variant="ghost"
        size="sm"
        onClick={onRetry}
        loading={retrying}
        className="mt-3"
      >
        {t("Reintentar")}
      </Button>
    </Alert>
  );
}
